import { useState } from 'react';
import { Check, FileText, History, Loader2, MinusCircle, RotateCcw, X } from 'lucide-react';
import moment from 'moment';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  useSubjectHistoryQuery,
  useVerificationChecklistQuery,
} from '@/client/api/verifications/verifications.query';
import {
  useChecklistItemReviewMutation,
  useChecklistItemUnwaiveMutation,
  useChecklistItemWaiveMutation,
} from '@/client/api/verifications/verifications.mutation';
import type { ChecklistItem, Verification, VerificationLifecycleAction } from '@/client/types/verifications';
import { ITEM_STATUS_BADGE, STATUS_BADGE, canDecide, canRevoke } from './verificationStatus';

// ponytail: documents are listed by id and count, not previewed. A viewer needs
// the signed-asset endpoint the ownership-claims surface uses; wire that in when
// reviewers say the ids are not enough.
const ItemBody = ({ item }: { item: ChecklistItem }) => {
  const files = Array.isArray(item.asid_json) ? item.asid_json : [];
  const value = item.value_json;

  if (files.length > 0) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-gray-600">
        <FileText className="h-3.5 w-3.5" />
        {files.length} file{files.length === 1 ? '' : 's'}
        <span className="text-gray-400">· asset {files.join(', ')}</span>
      </div>
    );
  }
  if (value && Object.keys(value).length > 0) {
    return (
      <dl className="mt-1 space-y-0.5 text-xs">
        {Object.entries(value).map(([k, v]) => (
          <div key={k} className="flex gap-2">
            <dt className="shrink-0 text-gray-400">{k}</dt>
            <dd className="truncate font-mono text-gray-700">{String(v)}</dd>
          </div>
        ))}
      </dl>
    );
  }
  return <p className="text-xs text-gray-400">Nothing submitted yet.</p>;
};

const ChecklistRow = ({ item, vid }: { item: ChecklistItem; vid: number }) => {
  const review = useChecklistItemReviewMutation();
  const waive = useChecklistItemWaiveMutation();
  const unwaive = useChecklistItemUnwaiveMutation();
  const [notes, setNotes] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const busy = review.isPending || waive.isPending || unwaive.isPending;

  // The API only accepts a decision on a submitted item, so offering one on a
  // pending item would just produce a 409.
  const decidable = item.status === 'submitted';

  return (
    <li className="rounded-lg border border-gray-200 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-gray-900">{item.label || item.item_key}</span>
            {!item.required && <span className="text-[10px] uppercase text-gray-400">optional</span>}
          </div>
          <p className="text-xs text-gray-400">{item.item_type.replace(/_/g, ' ')}</p>
          <div className="mt-1.5">
            <ItemBody item={item} />
          </div>
          {item.reviewer_notes && (
            <p className="mt-1.5 rounded bg-gray-50 p-1.5 text-xs text-gray-600">{item.reviewer_notes}</p>
          )}
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${ITEM_STATUS_BADGE[item.status]}`}>
          {item.status.replace(/_/g, ' ')}
        </span>
      </div>

      {decidable && (
        <div className="mt-2.5 space-y-2 border-t pt-2.5">
          {rejecting && (
            <textarea
              autoFocus
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="What is wrong with it? The applicant sees this."
              className="w-full rounded-md border border-gray-200 p-2 text-xs"
            />
          )}
          <div className="flex flex-wrap gap-1.5">
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => review.mutate({ vid, vcid: item.vcid, approved: true })}
            >
              <Check className="mr-1 h-3.5 w-3.5" /> Approve
            </Button>
            {rejecting ? (
              <Button
                size="sm"
                variant="destructive"
                disabled={busy || !notes.trim()}
                onClick={() =>
                  review.mutate(
                    { vid, vcid: item.vcid, approved: false, notes },
                    { onSuccess: () => { setRejecting(false); setNotes(''); } },
                  )
                }
              >
                Confirm reject
              </Button>
            ) : (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setRejecting(true)}>
                <X className="mr-1 h-3.5 w-3.5" /> Reject
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                const reason = window.prompt('Why does this item not apply?');
                if (reason?.trim()) waive.mutate({ vid, vcid: item.vcid, reason: reason.trim() });
              }}
            >
              <MinusCircle className="mr-1 h-3.5 w-3.5" /> Not applicable
            </Button>
          </div>
        </div>
      )}

      {item.status === 'not_applicable' && (
        <div className="mt-2 border-t pt-2">
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => unwaive.mutate({ vid, vcid: item.vcid })}>
            <RotateCcw className="mr-1 h-3.5 w-3.5" /> Undo waiver
          </Button>
        </div>
      )}
    </li>
  );
};

type Props = {
  verification: Verification | null;
  onClose: () => void;
  onAction: (verification: Verification, action: VerificationLifecycleAction) => void;
};

const VerificationReviewDrawer = ({ verification, onClose, onAction }: Props) => {
  const vid = verification?.vid ?? null;
  const { data: items, isLoading } = useVerificationChecklistQuery(vid);
  const { data: history } = useSubjectHistoryQuery(verification?.subject ?? null);

  const others = (history ?? []).filter((h) => h.vid !== vid);
  const outstanding = (items ?? []).filter((i) => i.required && i.status !== 'approved' && i.status !== 'not_applicable');

  return (
    <Sheet open={Boolean(verification)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex w-full flex-col overflow-y-auto sm:max-w-xl">
        {verification && (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                {verification.subject.label ?? `Verification ${verification.vid}`}
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[verification.status]}`}>
                  {verification.status.replace('_', ' ')}
                </span>
              </SheetTitle>
              <SheetDescription>
                {verification.type_name ?? 'unknown type'} · {verification.subject.kind ?? 'no subject'}
                {verification.subject.id ? ` ${verification.subject.id}` : ''} · vid {verification.vid} · opened{' '}
                {verification.date_added ? moment(verification.date_added).format('ll') : 'unknown'}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-5 flex-1 space-y-5">
              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Checklist
                  {items && items.length > 0 && (
                    <span className="ml-1.5 font-normal normal-case text-gray-400">
                      {items.length - outstanding.length} of {items.length} settled
                    </span>
                  )}
                </h3>
                {isLoading && <Skeleton className="h-24 w-full" />}
                {!isLoading && (items ?? []).length === 0 && (
                  <p className="text-sm text-gray-500">
                    This type has no checklist — it is decided as a whole.
                  </p>
                )}
                <ul className="space-y-2">
                  {(items ?? []).map((item) => (
                    <ChecklistRow key={item.vcid} item={item} vid={verification.vid} />
                  ))}
                </ul>
              </section>

              {others.length > 0 && (
                <section>
                  <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <History className="h-3.5 w-3.5" /> Earlier attempts ({others.length})
                  </h3>
                  <ul className="space-y-1">
                    {others.slice(0, 8).map((h) => (
                      <li key={h.vid} className="flex items-center justify-between gap-2 text-xs">
                        <span className="truncate text-gray-600">
                          {h.type_name ?? 'unknown'} · vid {h.vid}
                        </span>
                        <span className="flex shrink-0 items-center gap-2">
                          <span className={`rounded-full px-1.5 py-0.5 font-medium ${STATUS_BADGE[h.status]}`}>
                            {h.status.replace('_', ' ')}
                          </span>
                          <span className="text-gray-400">
                            {h.date_modified ? moment(h.date_modified).format('ll') : ''}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>

            <div className="sticky bottom-0 mt-4 border-t bg-white pt-3">
              {outstanding.length > 0 && canDecide(verification.status) && (
                <p className="mb-2 text-xs text-amber-700">
                  {outstanding.length} required item{outstanding.length === 1 ? '' : 's'} still unsettled.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                {verification.status === 'submitted' && (
                  <Button variant="outline" size="sm" onClick={() => onAction(verification, 'claim')}>
                    <Loader2 className="mr-1 h-3.5 w-3.5" /> Take for review
                  </Button>
                )}
                {canDecide(verification.status) && (
                  <>
                    <Button size="sm" onClick={() => onAction(verification, 'approve')}>
                      Approve verification
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => onAction(verification, 'reject')}>
                      Reject
                    </Button>
                  </>
                )}
                {canRevoke(verification.status) && (
                  <Button variant="destructive" size="sm" onClick={() => onAction(verification, 'revoke')}>
                    Revoke
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default VerificationReviewDrawer;
